import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { scrollToElement } from "@/utils/bs";
import { useSession } from "@/components/providers/session";
import Image from "next/image";
import Link from "next/link";

const CallbackModal = dynamic(() => import("@/components/callback-modal"));

type Props = {
  userId?: any;
  fields?: any;
  heading?: string;
  subheading?: string;
  text?: string;
  primary?: any;
  description?: string;
  event?: any;
  first?: boolean | undefined;
};

export default function Intro({
  fields,
  heading,
  subheading,
  text,
  first = false,
  description = "",
  primary,
  event,
}: Props) {
  const session = useSession();
  const router = useRouter();
  const [state, setState] = useState({
    faqs: false,
    courses: false,
    showCallbackModal: false,
  });

  useEffect(() => {
    if (window) {
      const f = document.getElementById("faqs");
      const p = document.getElementById("package-courses");
      setState({
        ...state,
        faqs: !!f,
        courses: !!p,
      });
    }
  }, []);

  const Title = ({ text }) => {
    return first ? (
      <h1 className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
        {text}
      </h1>
    ) : (
      <h3 className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
        {text}
      </h3>
    );
  };

  const events =
    event && event.events && event.events.length > 0
      ? event.events.filter((event) => {
          return (
            (event?.countStudentRegistrations?.aggregate?.count ?? 0) <
            (event?.seats ?? 0)
          );
        })
      : [];

  if (fields) {
    return (
      <div className="bg-white relative overflow-hidden">
        <div
          className="hidden sm:block sm:absolute sm:inset-y-0 sm:h-full sm:w-full"
          aria-hidden="true"
        >
          <div className="relative h-full max-w-7xl mx-auto">
            <svg
              className="absolute right-full transform translate-y-1/4 translate-x-1/4 lg:translate-x-1/2"
              width="404"
              height="784"
              fill="none"
              viewBox="0 0 404 784"
            >
              <defs>
                <pattern
                  id="f210dbf6-a58d-4871-961e-36d5016a0f49"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x="0"
                    y="0"
                    width="4"
                    height="4"
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width="404"
                height="784"
                fill="url(#f210dbf6-a58d-4871-961e-36d5016a0f49)"
              />
            </svg>
            <svg
              className="absolute left-full transform -translate-y-3/4 -translate-x-1/4 md:-translate-y-1/2 lg:-translate-x-1/2"
              width="404"
              height="784"
              fill="none"
              viewBox="0 0 404 784"
            >
              <defs>
                <pattern
                  id="5d0dd344-b041-4d26-bec4-8d33ea57ec9b"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x="0"
                    y="0"
                    width="4"
                    height="4"
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width="404"
                height="784"
                fill="url(#5d0dd344-b041-4d26-bec4-8d33ea57ec9b)"
              />
            </svg>
          </div>
        </div>
        <div className="relative max-w-5xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {fields.image && (
              <div className="w-44 mx-auto relative h-12 mb-4">
                <Image
                  src={`https:${fields.image.fields.file.url}`}
                  fill
                  alt={fields.title}
                  className="object-center object-cover"
                />
              </div>
            )}
            {fields.title && <Title text={fields.title} />}
            {fields.subtitle && (
              <h2 className="text-xl font-sans font-semibold text-brand-600 mt-4">
                {fields.subtitle}
              </h2>
            )}
            {fields.text && (
              <p className="max-w-xl mt-4 mx-auto text-base text-gray-500">
                {fields.text}
              </p>
            )}
            {fields.video && (
              <span className="block aspect-w-16 aspect-h-9 mt-16 rounded-lg shadow overflow-hidden">
                <iframe
                  className="w-full"
                  width="100%"
                  height="100%"
                  src={fields.video}
                  title="YouTube video player"
                  frameBorder="0"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </span>
            )}
          </div>
        </div>
        {primary && primary.fields.type && primary.fields.type === "Course" && (
          <section className="flex flex-col justify-center -mt-12 mb-12 px-4 md:-mt-4 lg:-mt-12 lg:mb-24 max-w-2xl mx-auto">
            <div className="flex flex-col md:flex-row pb-12 md:items-center justify-center">
              <div className="flex flex-col sm:flex-row justify-center items-center gap-2 relative z-10 rounded-md flex-1">
                <button
                  onClick={() => {
                    session.addCourse({
                      ...primary,
                      event,
                      package: event && event.package,
                      selectedEvent:
                        events && events.length > 0 && events[0]
                          ? events[0]
                          : null,
                    });
                    setTimeout(() => {
                      window.location.href = "/checkout";
                    }, 100);
                  }}
                  className="w-full flex items-center justify-center px-5 py-3 border border-transparent shadow text-base font-medium rounded-md text-white bg-brand-600 hover:bg-brand-900"
                >
                  Book Now
                </button>
                {/* <button
                  type="button"
                  onClick={() => {
                    setState({ ...state, showCallbackModal: true });
                  }}
                  className="mt-2 sm:mt-0 w-full flex items-center justify-center px-5 py-3 border border-brand-500 shadow text-base font-medium rounded-md text-brand-600 bg-white hover:bg-gray-100"
                >
                  Speak With An Advisor
                </button> */}
                <Link
                  href="/request-a-callback"
                  className="mt-2 sm:mt-0 w-full flex items-center justify-center px-5 py-3 border border-brand-500 shadow text-base font-medium rounded-md text-brand-600 bg-white hover:bg-gray-100 text-center"
                >
                  Speak With An Advisor
                </Link>
              </div>
            </div>
            <nav className="relative z-0 flex flex-col md:flex-row rounded-md -mt-4 space-y-2 md:space-y-0">
              {event && event.events && event.events.length === 0 && (
                <>
                  {state.faqs && (
                    <button
                      type="button"
                      onClick={() => {
                        const el = document.querySelector("#pricing");
                        el && scrollToElement(el);
                      }}
                      className="flex-1 -ml-px relative px-4 py-2 rounded md:rounded-none md:rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 text-center"
                    >
                      FAQs
                    </button>
                  )}
                  {state.courses && (
                    <button
                      type="button"
                      onClick={() => {
                        const el = document.querySelector("#package-courses");
                        el && scrollToElement(el);
                      }}
                      className={`flex-1 -ml-px relative px-4 py-2 ${
                        !state.faqs
                          ? "rounded md:rounded-none md:rounded-l-md"
                          : "rounded md:rounded-none"
                      } border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 text-center`}
                    >
                      Included Courses
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.querySelector("#pricing");
                      el && scrollToElement(el);
                    }}
                    className="flex-1 -ml-px relative px-4 py-2 rounded md:rounded-none md:rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 text-center"
                  >
                    Fees and booking
                  </button>
                </>
              )}
              {event && event.events && event.events.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.querySelector("#course-dates");
                      el && scrollToElement(el);
                    }}
                    className="relative px-4 py-2 rounded md:rounded-none md:rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex-1 text-center"
                  >
                    Course Dates
                  </button>
                  {state.faqs && (
                    <button
                      type="button"
                      onClick={() => {
                        const el = document.querySelector("#faqs");
                        el && scrollToElement(el);
                      }}
                      className="-ml-px relative px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex-1 text-center rounded md:rounded-none"
                    >
                      FAQs
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      const el = document.querySelector("#pricing");
                      el && scrollToElement(el);
                    }}
                    className="-ml-px relative px-4 py-2 rounded md:rounded-none md:rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex-1 text-center"
                  >
                    Fees and booking
                  </button>
                </>
              )}
              {state.showCallbackModal && (
                <CallbackModal
                  event={event}
                  showCallbackModal={state.showCallbackModal}
                  setShow={setState}
                />
              )}
            </nav>
          </section>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-5xl mx-auto pt-24 pb-24 px-4 sm:px-6 md:py-16 lg:py-18 lg:px-8">
        <div className="text-center">
          {subheading && (
            <h2 className="text-2xl font-sans font-semibold text-brand-600">
              {subheading}
            </h2>
          )}
          {heading && <Title text={heading} />}
          {text && (
            <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
              {text}
            </p>
          )}
          {description && (
            <p className="max-w-xl mx-auto text-lg text-gray-600 font-medium">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
